import { parseLocalDate, toIsoDate } from '../../../domain/utils/local-date';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, of } from 'rxjs';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { InterestRatesService } from '../../../domain/services/admin/interest-rates.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';

export interface InterestRate {
  id?: number;
  effectiveDate: string;
  endDate?: string;
  rate: number;
  method?: string;          // DAILY_FACTOR | MONTHLY_PERCENT
  dailyFactor?: number;
  monthlyRate?: number;
  description: string;
  status: string;
}

// Inline dialog component for add/edit
@Component({
  selector: 'app-interest-rate-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatDatepickerModule, MatNativeDateModule, MatButtonModule, MatIconModule, MatDialogModule],
  template: `
    <div class="dialog-container">
      <h2 mat-dialog-title class="dialog-title">
        <mat-icon class="title-icon">percent</mat-icon>
        {{ data.rate ? 'Edit Interest Rate' : 'Add Interest Rate' }}
      </h2>
      <mat-dialog-content>
        <form [formGroup]="form" class="rate-form">
          <div class="form-row">
            <div class="field-group">
              <label class="field-label">Rate (%) <span class="required">*</span></label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input matInput type="number" formControlName="rate" step="0.01" placeholder="Enter rate">
                <mat-error>Rate is required</mat-error>
              </mat-form-field>
            </div>
            <div class="field-group">
              <label class="field-label">Status <span class="required">*</span></label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-select formControlName="status">
                  <mat-option value="ACTIVE">Active</mat-option>
                  <mat-option value="INACTIVE">Inactive</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div class="form-row">
            <div class="field-group">
              <label class="field-label">Effective (start) date <span class="required">*</span></label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input matInput [matDatepicker]="picker" formControlName="effectiveDate" placeholder="Select date">
                <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
                <mat-error>Date is required</mat-error>
              </mat-form-field>
            </div>
            <div class="field-group">
              <label class="field-label">End date (blank = open)</label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input matInput [matDatepicker]="endPicker" formControlName="endDate" placeholder="Select date">
                <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
                <mat-datepicker #endPicker></mat-datepicker>
              </mat-form-field>
            </div>
          </div>
          <div class="form-row">
            <div class="field-group">
              <label class="field-label">Calculation method</label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-select formControlName="method">
                  <mat-option [value]="null">Legacy (annual rate)</mat-option>
                  <mat-option value="DAILY_FACTOR">Daily factor</mat-option>
                  <mat-option value="MONTHLY_PERCENT">Monthly percent</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
            <div class="field-group">
              <label class="field-label">Daily factor</label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input matInput type="number" formControlName="dailyFactor" step="0.000000001" placeholder="e.g. 0.000191781">
              </mat-form-field>
            </div>
          </div>
          <div class="form-row">
            <div class="field-group">
              <label class="field-label">Monthly rate</label>
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <input matInput type="number" formControlName="monthlyRate" step="0.0001" placeholder="e.g. 0.0100 = 1%/mo">
              </mat-form-field>
            </div>
          </div>
          <div class="field-group">
            <label class="field-label">Description</label>
            <mat-form-field appearance="outline" subscriptSizing="dynamic">
              <textarea matInput formControlName="description" rows="3" placeholder="Describe this interest rate"></textarea>
            </mat-form-field>
          </div>
        </form>
      </mat-dialog-content>
      <mat-dialog-actions align="end" class="dialog-actions">
        <button mat-stroked-button (click)="dialogRef.close()" class="cancel-btn">
          <mat-icon>arrow_back</mat-icon> GO BACK
        </button>
        <button mat-flat-button color="primary" [disabled]="form.invalid" (click)="save()" class="save-btn">
          <mat-icon>save</mat-icon> SAVE
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .dialog-container { padding: 8px; }
    .dialog-title { display: flex; align-items: center; gap: 12px; font-size: 20px; font-weight: 600; color: #1e293b; margin-bottom: 16px; }
    .title-icon { color: #2563eb; font-size: 24px; width: 24px; height: 24px; }
    .rate-form { display: flex; flex-direction: column; gap: 16px; min-width: 480px; }
    .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .field-group { display: flex; flex-direction: column; }
    .field-label { font-size: 13px; font-weight: 500; color: #475569; margin-bottom: 6px; }
    .required { color: #dc2626; }
    .field-group mat-form-field { width: 100%; }
    ::ng-deep .field-group .mat-mdc-text-field-wrapper { background: #fff; }
    ::ng-deep .field-group .mat-mdc-form-field-subscript-wrapper { min-height: 0; }
    .dialog-actions { padding: 16px 0 0; gap: 12px; }
    .cancel-btn { color: #2563eb; }
    .save-btn mat-icon, .cancel-btn mat-icon { font-size: 18px; width: 18px; height: 18px; margin-right: 4px; }
  `]
})
export class InterestRateDialogComponent {
  form: FormGroup;
  constructor(
    public dialogRef: MatDialogRef<InterestRateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { rate?: InterestRate }
  ) {
    this.form = new FormGroup({
      rate: new FormControl(data.rate?.rate ?? null, [Validators.required]),
      effectiveDate: new FormControl(parseLocalDate(data.rate?.effectiveDate), [Validators.required]),
      endDate: new FormControl(parseLocalDate(data.rate?.endDate)),
      method: new FormControl(data.rate?.method ?? null),
      dailyFactor: new FormControl(data.rate?.dailyFactor ?? null),
      monthlyRate: new FormControl(data.rate?.monthlyRate ?? null),
      description: new FormControl(data.rate?.description ?? null),
      status: new FormControl(data.rate?.status ?? 'ACTIVE', [Validators.required]),
    });
  }
  save() {
    const val = this.form.value;
    const fmt = (d: any) => toIsoDate(d);
    this.dialogRef.close({ ...val, effectiveDate: fmt(val.effectiveDate), endDate: fmt(val.endDate) });
  }
}

@Component({
  selector: 'app-interest-rates',
  standalone: true,
  imports: [
    CommonModule, MatTableModule, MatButtonModule, MatIconModule, MatCardModule,
    MatPaginatorModule, MatSortModule, MatProgressSpinnerModule,
  ],
  templateUrl: './interest-rates.component.html',
  styleUrl: './interest-rates.component.css',
})
export class InterestRatesComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<InterestRate>;

  displayedColumns = ['effectiveDate', 'rate', 'description', 'status', 'actions'];
  dataSource = new MatTableDataSource<InterestRate>([]);

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly interestRatesService: InterestRatesService,
    private readonly dialog: MatDialog,
    private readonly dialogService: DialogService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/admin/interest-rates', label: 'Administrative' },
      { icon: 'percent', url: '/admin/interest-rates', label: 'Interest Rates' },
    ]);
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  loadData(): void {
    this.interestRatesService.getAll()
      .pipe(catchError(() => of([])))
      .subscribe((data) => { this.dataSource.data = data; });
  }

  addRate(): void {
    const dialogRef = this.dialog.open(InterestRateDialogComponent, {
      width: '580px', data: {}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.interestRatesService.create(result).subscribe(() => this.loadData());
      }
      // ponytail: cancelling out of the dialog via "GO BACK" left the page-level
      // "ADD RATE" button visually gone until an unrelated click forced a repaint
      // (stale CDK overlay teardown not reconciled by Angular). Force a CD pass on
      // every close, not just the save path, so the header always repaints.
      this.cdr.markForCheck();
    });
  }

  editRate(rate: InterestRate): void {
    const dialogRef = this.dialog.open(InterestRateDialogComponent, {
      width: '580px', data: { rate }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && rate.id) {
        this.interestRatesService.update(rate.id, result).subscribe(() => this.loadData());
      }
      this.cdr.markForCheck();
    });
  }

  deleteRate(rate: InterestRate): void {
    if (!rate.id) return;
    this.dialogService.openDeletedDialog(
      'Delete Interest Rate',
      `Are you sure you want to delete the ${rate.rate}% rate? This action cannot be undone.`,
      (confirmed: boolean) => {
        if (confirmed) {
          this.interestRatesService.delete(rate.id!).subscribe(() => this.loadData());
        }
      }
    );
  }
}
